import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { MuscleBodies, MUSCLE_COLORS as COLORS } from './MuscleBodies';

const GYM_NAME = 'Betera Training Center';

const T = {
  es: { top: 'Músculos trabajados', sets: 'series', footer: 'Mapa muscular · generado con la app de', front: 'Frente', back: 'Espalda', saved: 'Imagen descargada', error: 'No se pudo generar la imagen' },
  en: { top: 'Muscles trained', sets: 'sets', footer: 'Muscle map · generated with the app of', front: 'Front', back: 'Back', saved: 'Image downloaded', error: 'Could not generate the image' },
};

export const useMuscleShare = (fileBase = 'muscle-map') => {
  const shareRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const share = useCallback(async () => {
    if (!shareRef.current || sharing) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(shareRef.current, { pixelRatio: 2, backgroundColor: '#0b0b0f', cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const name = `${fileBase.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
      const file = new File([blob], name, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fileBase });
      } else {
        const a = document.createElement('a');
        a.href = dataUrl; a.download = name; a.click();
        toast.success(T.es.saved);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error(T.es.error);
    } finally {
      setSharing(false);
    }
  }, [fileBase, sharing]);

  return { shareRef, sharing, share };
};

export const MuscleShareCard = forwardRef(({ data, title, date, language = 'es' }, ref) => {
  const t = T[language] || T.es;
  const top = data?.regions || [];
  const max = data?.max_score || 1;
  return (
    <div style={{ position: 'fixed', left: '-20000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
      <div ref={ref} data-testid="muscle-share-card" style={{ width: 540, padding: 32, background: '#0b0b0f', color: '#f4f4f5', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#f87171', fontWeight: 700 }}>{GYM_NAME}</div>
          <div style={{ fontSize: 13, color: '#a1a1aa' }}>{date}</div>
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, marginTop: 8, marginBottom: 20 }}>{title}</div>
        <MuscleBodies data={data} labels={{ front: t.front, back: t.back }} className="!gap-6" />
        <div style={{ marginTop: 24, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a1a1aa' }}>{t.top}</div>
        <div style={{ marginTop: 10 }}>
          {top.map((r) => (
            <div key={r.slug} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0', fontSize: 14 }}>
              <span style={{ width: 170 }}>{r.label}</span>
              <span style={{ flex: 1, height: 10, borderRadius: 5, background: '#27272a', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${(r.score / max) * 100}%`, background: COLORS[r.level - 1], borderRadius: 5 }} />
              </span>
              <span style={{ width: 70, textAlign: 'right', color: '#a1a1aa', fontSize: 12 }}>{r.score} {t.sets}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 24, fontSize: 11, color: '#71717a', textAlign: 'center' }}>{t.footer} {GYM_NAME}</div>
      </div>
    </div>
  );
});
MuscleShareCard.displayName = 'MuscleShareCard';
